/**
 * Bulk policy verification — types, API calls and the pure helpers the page
 * uses. Kept out of `services/policies.ts` so the page owns its own surface.
 *
 * The backend only ever checks the caller's OWN broker's policies, so a number
 * held by a different broker comes back as "missing" exactly like one that
 * exists nowhere. The UI must therefore never phrase a missing result as
 * "this policy does not exist" — only as "not found in your policies".
 */
import api from '@/services/api';

export const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'] as const;
/** Mirrors MAX_UPLOAD_BYTES in backend/apps/policies/bulk_verify.py. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface BulkVerifyMatch {
  policy_id: string;
  policy_number: string;
  insurer_policy_number: string;
  matched_on: 'policy_number' | 'insurer_policy_number' | 'legacy_policy_number';
  status: string;
  holder_name: string;
  inception_date: string | null;
  expiry_date: string | null;
}

export interface BulkVerifyItem {
  number: string;
  occurrences: number;
  rows: number[];
  exists: boolean;
  match: BulkVerifyMatch | null;
}

export interface BulkVerifySummary {
  total_values: number;
  unique_numbers: number;
  duplicate_numbers: number;
  duplicate_values: number;
  found_count: number;
  missing_count: number;
  rows_truncated: boolean;
  column_header: string | null;
}

export interface BulkVerifyResponse {
  summary: BulkVerifySummary;
  results: BulkVerifyItem[];
  missing: BulkVerifyItem[];
  duplicates: BulkVerifyItem[];
  results_truncated: boolean;
  missing_truncated: boolean;
}

export type ResultTab = 'missing' | 'found' | 'duplicates' | 'all';

/**
 * Client-side pre-check so an obviously bad file never costs a round trip.
 * The backend repeats both checks — this is convenience, not enforcement.
 */
export function validateUploadFile(file: File | null): string | null {
  if (!file) return 'Choose a file to verify.';
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return 'Unsupported file type. Upload a .csv, .xlsx or .xls file.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File too large. Maximum size is ${MAX_UPLOAD_BYTES / (1024 * 1024)}MB.`;
  }
  if (file.size === 0) return 'That file is empty.';
  return null;
}

/** Rows to show for the selected tab. */
export function filterResults(response: BulkVerifyResponse | null, tab: ResultTab): BulkVerifyItem[] {
  if (!response) return [];
  switch (tab) {
    case 'missing':
      return response.missing;
    case 'duplicates':
      return response.duplicates;
    case 'found':
      return response.results.filter((item) => item.exists);
    case 'all':
    default:
      return response.results;
  }
}

/** "Rows 2, 5, 9" — where in the uploaded file a number appeared. */
export function formatRows(rows: number[], limit = 8): string {
  if (rows.length === 0) return '—';
  const shown = rows.slice(0, limit).join(', ');
  return rows.length > limit ? `${shown} +${rows.length - limit} more` : shown;
}

const MATCHED_ON_LABELS: Record<BulkVerifyMatch['matched_on'], string> = {
  policy_number: 'Our policy number',
  insurer_policy_number: 'Insurer policy number',
  legacy_policy_number: 'Legacy policy number',
};

export function matchedOnLabel(matchedOn: BulkVerifyMatch['matched_on']): string {
  return MATCHED_ON_LABELS[matchedOn] ?? matchedOn;
}

/** One-line verdict for the results banner. */
export function verdictText(summary: BulkVerifySummary): string {
  // Both the noun and the verb have to agree with the count being described:
  // the "all found" line counts unique numbers, the "not found" line counts
  // missing ones, so they do not always agree with each other.
  const plural = (count: number) => (count === 1 ? '' : 's');
  const verb = (count: number) => (count === 1 ? 'was' : 'were');

  if (summary.missing_count === 0) {
    const total = summary.unique_numbers;
    return `All ${total} policy number${plural(total)} ${verb(total)} found in your policies.`;
  }
  const missing = summary.missing_count;
  return (
    `${missing} of ${summary.unique_numbers} policy number${plural(summary.unique_numbers)} ` +
    `${verb(missing)} not found in your policies.`
  );
}

// --------------------------------------------------------------------------
// API
// --------------------------------------------------------------------------

export async function verifyPolicyNumbers(file: File): Promise<BulkVerifyResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<BulkVerifyResponse>('/policies/bulk-verify/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

function triggerDownload(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}

export function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  const match = disposition?.match(/filename="?([^"]+)"?/);
  return match?.[1] || fallback;
}

/**
 * Download the report of missing numbers.
 *
 * Re-posts the SAME file with `file_format`, so the report is generated by the
 * one backend code path rather than rebuilt from the (payload-capped) inline
 * JSON — on a file big enough to truncate `missing`, a client-side rebuild
 * would silently ship a short report.
 */
export async function downloadMissingReport(file: File, fileFormat: 'csv' | 'xlsx'): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/policies/bulk-verify/?file_format=${fileFormat}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  });
  const filename = filenameFromDisposition(
    response.headers?.['content-disposition'] as string | undefined,
    `missing_policies.${fileFormat}`
  );
  triggerDownload(new Blob([response.data]), filename);
}

export async function downloadVerifyTemplate(fileFormat: 'csv' | 'xlsx'): Promise<void> {
  const response = await api.get(`/policies/bulk-verify-template/?file_format=${fileFormat}`, {
    responseType: 'blob',
  });
  const filename = filenameFromDisposition(
    response.headers?.['content-disposition'] as string | undefined,
    `policy_verification_template.${fileFormat}`
  );
  triggerDownload(new Blob([response.data]), filename);
}
